import global from "../../../common/utility/global";
import { NOMBRES_MES_CORTO } from "./constants";

export function formatCurrency(value, moneda = "PEN") {
    const numero = Number(value) || 0;
    const simbolo = moneda === "USD" ? "US$" : "S/";
    return `${simbolo} ${global.decimalFormat(numero.toFixed(2))}`;
}

export function formatNumber(value, decimales = 1) {
    const numero = Number(value) || 0;
    return global.decimalFormat(numero.toFixed(decimales));
}

// Variacion de un KPI sin signo: la flecha de la tarjeta ya indica si sube
// o baja (p. ej. "8.3%" con flecha roja hacia abajo).
export function formatTendencia(pct) {
    return `${formatNumber(Math.abs(Number(pct) || 0))}%`;
}

// Etiqueta de eje para montos: "12K" desde 1000; con montos chicos dividir
// entre 1000 siempre daria "0K", ahi se muestra el numero tal cual.
export function formatMontoEje(val) {
    return val >= 1000 ? `${Math.round(val / 1000)}K` : `${Math.round(val)}`;
}

const MAX_CARACTERES_NOMBRE = 18;

// Recorta nombres largos para que la leyenda mantenga siempre el mismo
// ancho/tamano; sin esto, un proveedor con razon social muy larga estira
// la leyenda y deforma el layout del grafico (dona corrida, barras
// desalineadas, etc.).
export function truncarNombre(nombre) {
    if (!nombre) return "";
    return nombre.length > MAX_CARACTERES_NOMBRE
        ? `${nombre.slice(0, MAX_CARACTERES_NOMBRE)}...`
        : nombre;
}

// "2026-08-27" -> Date local (evita el corrimiento de dia que da
// "new Date('2026-08-27')" al interpretarse como UTC en un huso horario negativo).
function parsearFechaLocal(fechaStr) {
    const [anio, mes, dia] = fechaStr.slice(0, 10).split("-").map(Number);
    return new Date(anio, mes - 1, dia);
}

function obtenerClaveTiempo(fecha, esMismoMes) {
    return esMismoMes
        ? `${fecha.getDate()} ${NOMBRES_MES_CORTO[fecha.getMonth()]}`
        : `${NOMBRES_MES_CORTO[fecha.getMonth()]} ${fecha.getFullYear()}`;
}

const redondear2 = (valor) => Math.round(valor * 100) / 100;

// Arma la "Evolucion" a partir de "historial": una fila por dia del periodo
// (el backend ya completa con 0 los dias sin compras) con el monto del
// periodo actual y el del periodo anterior alineado al mismo dia. Se agrupa
// por dia si el filtro cae en un mes, o por mes si abarca mas de uno.
function construirEvolucion(historial, filter) {
    const esMismoMes = filter.anioInicio === filter.anioFin && filter.mesInicio === filter.mesFin;

    const ordenClaves = [];
    const acumulado = {};
    historial
        .map((fila) => ({ ...fila, __fecha: parsearFechaLocal(fila.fecha) }))
        .sort((a, b) => a.__fecha - b.__fecha)
        .forEach((fila) => {
            const clave = obtenerClaveTiempo(fila.__fecha, esMismoMes);
            if (!acumulado[clave]) {
                acumulado[clave] = { pen: 0, usd: 0, penAnterior: 0, usdAnterior: 0 };
                ordenClaves.push(clave);
            }
            acumulado[clave].pen += Number(fila.montoSoles) || 0;
            acumulado[clave].usd += Number(fila.montoDolares) || 0;
            acumulado[clave].penAnterior += Number(fila.montoSolesAnterior) || 0;
            acumulado[clave].usdAnterior += Number(fila.montoDolaresAnterior) || 0;
        });

    const construirSeries = (moneda) => [
        { name: "Periodo actual", data: ordenClaves.map((clave) => redondear2(acumulado[clave][moneda])) },
        { name: "Periodo anterior", data: ordenClaves.map((clave) => redondear2(acumulado[clave][`${moneda}Anterior`])) },
    ];

    return {
        timeLine: ordenClaves,
        pen: construirSeries("pen"),
        usd: construirSeries("usd"),
    };
}

// Suma por nombre las filas {nombre, totalSoles, totalDolares}. El backend
// ya las manda agrupadas, pero se acumula igual por si llega mas de una
// fila para el mismo nombre (p. ej. separadas por fecha).
function acumularPorNombre(filas, nombrePorDefecto) {
    const acumulado = {};
    const orden = [];
    filas.forEach((fila) => {
        const nombre = fila.nombre || nombrePorDefecto;
        if (!acumulado[nombre]) {
            acumulado[nombre] = { nombre, pen: 0, usd: 0 };
            orden.push(nombre);
        }
        acumulado[nombre].pen += Number(fila.totalSoles) || 0;
        acumulado[nombre].usd += Number(fila.totalDolares) || 0;
    });
    return orden.map((nombre) => acumulado[nombre]);
}

// Top 10 de proveedores ordenado por la moneda que se muestra: ordenar
// solo por soles dejaria en dolares un ranking que no corresponde.
function construirTopProveedores(proveedores, moneda) {
    return [...proveedores]
        .sort((a, b) => b[moneda] - a[moneda])
        .slice(0, 10)
        .filter((item) => item[moneda] > 0)
        .map((item) => ({ nombre: item.nombre, total: item[moneda] }));
}

// Dona de categorias: el backend ya agrupa las menores en "Otros"; aqui se
// calcula el % por moneda para que cuadre al 100% tambien en dolares, y se
// omiten las categorias sin monto en esa moneda.
function construirCategorias(categorias, moneda) {
    const totalGeneral = categorias.reduce((acumulado, item) => acumulado + item[moneda], 0);
    return categorias
        .map((item) => ({
            nombre: item.nombre,
            total: item[moneda],
            porcentaje: totalGeneral > 0 ? (item[moneda] / totalGeneral) * 100 : 0,
        }))
        .filter((item) => item.total > 0);
}

function direccionTendencia(actual, anterior) {
    if (actual > anterior) return "up";
    if (actual < anterior) return "down";
    return "flat";
}

// % de variacion actual vs anterior. Si el periodo anterior no tuvo
// movimiento, se evita la division entre 0: sin cambio real (0/0) queda
// plano, y una aparicion nueva (valor/0) se marca como +100%.
function calcularTendenciaPct(actual, anterior) {
    if (!anterior) return actual > 0 ? 100 : 0;
    return ((actual - anterior) / anterior) * 100;
}

// Sirve tanto para montos como para conteos (proveedores, comprobantes).
function construirKpi(actual, anterior) {
    const valorActual = Number(actual) || 0;
    const valorAnterior = Number(anterior) || 0;
    return {
        valor: valorActual,
        tendenciaPct: calcularTendenciaPct(valorActual, valorAnterior),
        tendenciaDireccion: direccionTendencia(valorActual, valorAnterior),
    };
}

export function buildDashboardData(response, filter) {
    // El servicio real (compraService.dashboardCompra) devuelve:
    // - kpis: arreglo de un solo elemento con los totales del periodo y su
    //   comparacion contra el periodo anterior (sufijo "Anterior"). Los
    //   montos llegan con nombre "Ventas" (totalVentasSoles,
    //   ventasPromedioActualSoles...) pero corresponden a compras;
    //   ventasPromedio es el monto promedio por comprobante.
    // - compras: total por proveedor (nombre, totalSoles, totalDolares).
    // - categorias: total por categoria, con las menores agrupadas en "Otros".
    // - historial: una fila por dia con montoSoles/montoDolares del periodo
    //   y montoSolesAnterior/montoDolaresAnterior del periodo anterior.
    const kpisApi = (response && response.kpis && response.kpis[0]) || {};
    const proveedores = acumularPorNombre((response && response.compras) || [], "Sin proveedor");
    const categorias = acumularPorNombre((response && response.categorias) || [], "Sin categoría");
    const historial = (response && response.historial) || [];

    return {
        kpis: {
            totalCompras: {
                pen: construirKpi(kpisApi.totalVentasSoles, kpisApi.totalVentasSolesAnterior),
                usd: construirKpi(kpisApi.totalVentasDolares, kpisApi.totalVentasDolaresAnterior),
            },
            proveedoresActivos: construirKpi(kpisApi.proveedoresActivos, kpisApi.proveedoresActivosAnterior),
            comprobantes: construirKpi(kpisApi.cantidadCPE, kpisApi.cantidadCPEAnterior),
            compraPromedio: {
                pen: construirKpi(kpisApi.ventasPromedioActualSoles, kpisApi.ventasPromedioAnteriorSoles),
                usd: construirKpi(kpisApi.ventasPromedioActualDolares, kpisApi.ventasPromedioAnteriorDolares),
            },
            proveedoresNuevos: construirKpi(kpisApi.proveedoresNuevos, kpisApi.proveedoresNuevosAnterior),
        },
        topProveedores: {
            pen: construirTopProveedores(proveedores, "pen"),
            usd: construirTopProveedores(proveedores, "usd"),
        },
        categorias: {
            pen: construirCategorias(categorias, "pen"),
            usd: construirCategorias(categorias, "usd"),
        },
        evolucion: construirEvolucion(historial, filter),
    };
}
